/**
 * <gldn-flora-explore> — flower picker with filter tabs + horizontal
 * scroll carousel. No jQuery, no Slick.
 *
 * Markup contract (see sections/gldn-flora-explore.liquid):
 *   <gldn-flora-explore data-active-group="all_flowers">
 *     <div class="gldn-flora-explore__filters">
 *       <button data-group-key="all_flowers" class="is-active">All Flowers</button>
 *       <button data-group-key="our_favorites">Our Favorites</button>
 *       ...
 *     </div>
 *     <div class="gldn-flora-explore__row-wrap">
 *       <button data-prev>‹</button>
 *       <div data-row>
 *         <a data-flower data-flower-handle="aster" data-groups="all_flowers our_favorites">Aster</a>
 *         ...
 *       </div>
 *       <button data-next>›</button>
 *     </div>
 *   </gldn-flora-explore>
 *
 * Behavior:
 *   - Click filter button → toggle is-active class + update host's
 *     data-active-group attribute. The applyFilter() method hides
 *     flower items whose data-groups doesn't include the active key.
 *   - Click prev/next arrow → scroll row by one item width.
 *   - On connect, check window.location.pathname for any flower
 *     handle; if found, mark that item is-active and scroll it into
 *     view.
 */
class GldnFloraExplore extends HTMLElement {
  #row;
  #items;
  #filters;
  #prev;
  #next;

  connectedCallback() {
    this.#row = this.querySelector('[data-row]');
    this.#items = Array.from(this.querySelectorAll('[data-flower]'));
    this.#filters = Array.from(this.querySelectorAll('[data-group-key]'));
    this.#prev = this.querySelector('[data-prev]');
    this.#next = this.querySelector('[data-next]');

    this.#filters.forEach((btn) => {
      btn.addEventListener('click', this.#onFilterClick);
    });

    this.#prev?.addEventListener('click', () => this.#scroll(-1));
    this.#next?.addEventListener('click', () => this.#scroll(1));

    this.#highlightActiveFromPath();
    this.applyFilter(this.dataset.activeGroup || 'all_flowers');
    this.#updateArrows();
    this.#row?.addEventListener('scroll', this.#updateArrows, { passive: true });
  }

  #onFilterClick = (event) => {
    const btn = event.currentTarget;
    const key = btn.dataset.groupKey;
    if (!key) return;
    this.#filters.forEach((b) => {
      const active = b === btn;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.dataset.activeGroup = key;
    this.applyFilter(key);
    this.#updateArrows();
  };

  /** Show/hide items based on the active group. */
  applyFilter(key) {
    this.#items.forEach((item) => {
      const groups = (item.dataset.groups || '').split(/\s+/);
      const visible = key === 'all_flowers' || groups.includes(key);
      item.classList.toggle('is-hidden', !visible);
    });
    if (this.#row) this.#row.scrollLeft = 0;
  }

  #scroll(direction) {
    if (!this.#row) return;
    const firstVisible = this.#items.find((i) => !i.classList.contains('is-hidden'));
    if (!firstVisible) return;
    const itemWidth = firstVisible.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this.#row).columnGap || '0') || 0;
    this.#row.scrollBy({ left: direction * (itemWidth + gap) * 2, behavior: 'smooth' });
  }

  #updateArrows = () => {
    if (!this.#row) return;
    const maxScroll = this.#row.scrollWidth - this.#row.clientWidth - 1;
    if (this.#prev) this.#prev.disabled = this.#row.scrollLeft <= 0;
    if (this.#next) this.#next.disabled = this.#row.scrollLeft >= maxScroll;
  };

  #highlightActiveFromPath() {
    const path = window.location.pathname;
    let activeItem = null;
    this.#items.forEach((item) => {
      const handle = item.dataset.flowerHandle;
      const isActive = handle && path.includes('/' + handle);
      item.classList.toggle('is-active', isActive);
      if (isActive) activeItem = item;
    });
    if (activeItem && this.#row) {
      // Scroll active flower roughly into view, biased toward the start.
      const itemRect = activeItem.getBoundingClientRect();
      const rowRect = this.#row.getBoundingClientRect();
      const offset = itemRect.left - rowRect.left - 24;
      this.#row.scrollLeft = offset;
    }
  }
}

if (!customElements.get('gldn-flora-explore')) {
  customElements.define('gldn-flora-explore', GldnFloraExplore);
}
